function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

/* ☕ Variant Selector Functionality ☕ */
class VariantSelects extends HTMLElement { 
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    // Changes
    // Pass the picker itself, not the single value that changed, so the gate can
    // read the whole selected variant rather than guessing it from one option.
    HandleVariantChange(this);

    // console.log(target)

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

/*
  ☕ Kezley Code ☕
*/

/*
  ☕ Restricted Option Values ☕

  The option values that mean "yes, send this to Katt.Kanna to be filled with
  complimentary gifts", which is the choice that requires a member account or a
  Day Pass.

  This lives here rather than with the product page code because the cart check
  below needs it too, and the cart is not a product page.

  NOTE: snippets/buy-buttons.liquid does the same check in Liquid for the state the
  product page is first drawn in. If this list changes, change it there too.
*/
const RESTRICTED_OPTION_VALUES = ["yes"];

/*
  ☕ Is Restricted Option Value ☕

  Compares a single option value against the list above, ignoring case and any stray
  spaces, so "Yes", "YES" and "yes " all count.
*/
function IsRestrictedOptionValue(value) {
  return RESTRICTED_OPTION_VALUES.indexOf(String(value).trim().toLowerCase()) !== -1;
}

/*
  ☕ Has The Customer Got A Day Pass ☕

  window.dayPass is built in theme.liquid and says whether a Day Pass is sitting in
  the basket right now. Reading it through a function rather than directly means the
  gate does not care where the answer comes from.
*/
function HasDayPassInCart() {
  return window.dayPass != null && window.dayPass.inCart === true;
}

/*
  ☕ Is The Customer Allowed The Gift Option ☕

  There are two ways to earn it: be a member, or have a Day Pass in the basket. This
  is the one place that decides, so the page load code and the variant change code
  cannot drift apart on the answer.

  If the logged in flag is missing for any reason, the customer counts as logged out,
  so a broken page leaves the gate up rather than falling open.
*/
function CustomerHasGiftAccess() {
  const isUserLoggedIn = document.querySelector("#is-user-logged-in")?.value === "true";
  return isUserLoggedIn || HasDayPassInCart();
}

/*
  ☕ This function is called whenever the in-built theme code for when the user swaps variants
  is run. It takes in the variant picker the user just used (which is the argument passed in)
  and uses that, with the variantTracker, to work out what needs to be shown to the user. ☕

  It used to take just the single option value that had changed, and the tracker tried to
  work the rest of the variant out from it by guessing which option was the colour. That
  guess was wrong on any product with more than two options. Passing the picker lets the
  tracker read the whole selection instead, so there is nothing left to guess at. ☕
*/
function HandleVariantChange(variantSelects) {
  // If the user cannot purchase at all, don't do anything.
  const userCanBuyProduct = document.querySelector("#user-can-buy-product")?.value == "true";
  if (userCanBuyProduct == false) return;

  /*
    The Membership product's date of birth check used to be re-run from here, off a
    selector that never matched anything, so it had quietly never fired. Running it
    at this point does not work anyway: the theme rebuilds the product info straight
    afterwards and switches the add to cart button back on. It now re-runs from
    main-product.liquid on the 'variant-change' event, which fires after that
    rebuild, so it is the last word on the button rather than the first.
  */

  const currentOptionRequiresLogin = variantTracker.IsThisVariantRestricted(variantSelects);
  const showBuyNowButton = currentOptionRequiresLogin == false || CustomerHasGiftAccess()

  // console.log(`isUserLoggedIn: ${isUserLoggedIn}`);
  // console.log(`currentOptionRequiresLogin: ${currentOptionRequiresLogin}`);
  // console.log(`showBuyNowButton: ${showBuyNowButton}`); 

  if (showBuyNowButton) {
    ShowBuyNowButton();
    HideLoginRequiredContainer();
  } else {
    ShowLoginRequiredContainer();
    HideBuyNowButton();
  }
}

/*
  ☕ The four functions below swap between the buy button and the member message. ☕

  base.css keeps both containers hidden by default and only ".show" brings one back,
  so adding and removing that single class is all that is needed. These used to also
  write a ".hide" class, which had no style rule anywhere and did nothing.
*/

/*
  ☕ Gets the buy now container and adds the show class.☕
*/
function ShowBuyNowButton() {
  const buyButtonContainer = document.querySelector("#buy-button-container");
  buyButtonContainer.classList.add("show");
}

/*
  ☕ Gets the buy now container and removes the show class.☕
*/
function HideBuyNowButton() {
  const buyButtonContainer = document.querySelector("#buy-button-container");
  buyButtonContainer.classList.remove("show");
}

/*
  ☕ Gets the show login container and adds the show class.☕
*/

function ShowLoginRequiredContainer() {
  const loginRequiredContainer = document.querySelector("#login-required-container");
  loginRequiredContainer.classList.add("show");
}

/*
  ☕ Gets the show login container and removes the show class.☕
*/
function HideLoginRequiredContainer() {
  const loginRequiredContainer = document.querySelector("#login-required-container");
  loginRequiredContainer.classList.remove("show");
}


/*
  ☕ Runs when the document is loaded, runs the initial logic to deduce which option
  should be shown to the user when the page is loaded and then displays that container.☕
*/
document.addEventListener('DOMContentLoaded', function() {
  // If the user cannot purchase at all, don't do anything.
  const userCanBuyProduct = document.querySelector("#user-can-buy-product")?.value == "true";
  if (userCanBuyProduct == false) return;

  const onPageLoadLoginRequiredInput = document.querySelector("#on-page-load-login-required-input");
  const productIsInStock = onPageLoadLoginRequiredInput.dataset.isInStock == "true";
  const pageRequiresLogin = onPageLoadLoginRequiredInput.value == "true";

  SetUpDayPassButton();

  HideBuyNowButton();
  HideLoginRequiredContainer();

  /*
    pageRequiresLogin is worked out in Liquid, which only knows whether the customer
    is a member. A Day Pass in the basket earns the same access, so it is checked here
    as well, otherwise someone who has already bought one would still be told to sign
    up every time they opened a product.
  */
  if (productIsInStock == false) ShowBuyNowButton();
  else if (pageRequiresLogin && CustomerHasGiftAccess() == false) ShowLoginRequiredContainer();
  else ShowBuyNowButton();

});

/*
  ☕ Day Pass Button ☕

  The Day Pass button is the first of the three ways past the "You must be a Member"
  message. It puts a Day Pass in the basket, which unlocks the gift option for this
  order without the customer having to join.

  It only appears when it can actually be used, so it stays hidden if the Day Pass
  product is missing or out of stock, and if one is already in the basket (in which
  case the customer is not being shown this message anyway).
*/
function SetUpDayPassButton() {
  const dayPassButton = document.querySelector("#buy-day-pass-button");
  if (!dayPassButton) return;

  const dayPass = window.dayPass;
  if (!dayPass || !dayPass.variantId || dayPass.available === false) return;
  if (dayPass.inCart === true) return;

  // Say what it costs. A button that spends money should be honest about it.
  const dayPassLabel = document.querySelector("#buy-day-pass-label");
  if (dayPassLabel && dayPass.priceFormatted) {
    dayPassLabel.textContent = `Purchase a Day Pass (${dayPass.priceFormatted})`;
  }

  dayPassButton.hidden = false;
  dayPassButton.addEventListener("click", OnDayPassButtonClick);
}

/*
  ☕ Asks for the customer's details, then adds the Day Pass to the basket. ☕

  The pass is not sold on its own. Age has to be confirmed and an ID number given
  first, so pressing this button opens the details panel rather than going straight to
  the basket. The panel calls back here once it has both answers.

  Only the Day Pass goes in. The product the customer is looking at is left alone, so
  nothing lands in the basket that they did not ask for. Once the pass is in, the gate
  is re-run and the normal Add to Cart button takes the place of this message.
*/
function OnDayPassButtonClick(event) {
  const dayPassButton = event.currentTarget;

  // Guard against a double click opening the panel twice over.
  if (dayPassButton.getAttribute("aria-busy") === "true") return;
  HideDayPassError();

  OpenDayPassDetails(async function (properties) {
    dayPassButton.setAttribute("aria-busy", "true");
    dayPassButton.disabled = true;

    try {
      const response = await AddDayPassToCart(properties, "cart-icon-bubble");

      window.dayPass.inCart = true;
      UpdateCartIconBubble(response);
      publish(PUB_SUB_EVENTS.cartUpdate, { source: "day-pass-button", cartData: response });

      // Re-ask the gate now that the basket has changed.
      HandleVariantChange(document.querySelector("variant-selects"));
    } catch (error) {
      // Put the button back so they can try again, and let the panel report it.
      dayPassButton.disabled = false;
      dayPassButton.removeAttribute("aria-busy");
      throw error;
    }
  });
}

/*
  ☕ Puts one Day Pass in the basket, carrying the customer's details with it. ☕

  The details ride along as line item properties, which is the same mechanism the
  engraving name uses. They show against the line in the basket, at checkout, and on
  the order in Shopify.
*/
async function AddDayPassToCart(properties, sections) {
  const body = { items: [{ id: window.dayPass.variantId, quantity: 1, properties: properties }] };
  if (sections) body.sections = sections;

  const response = await (
    await fetch(window.routes.cart_add_url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/javascript" },
      body: JSON.stringify(body),
    })
  ).json();

  // The cart API answers 200 with a status field on it when it refuses.
  if (response.status) throw new Error(response.description || response.message);
  return response;
}

/*
  ☕ Keeps the basket count in the header honest after a Day Pass is added. ☕
*/
function UpdateCartIconBubble(response) {
  const sectionHtml = response && response.sections && response.sections["cart-icon-bubble"];
  if (!sectionHtml) return;

  const cartIconBubble = document.querySelector("#cart-icon-bubble");
  if (!cartIconBubble) return;

  const renderedSection = new DOMParser()
    .parseFromString(sectionHtml, "text/html")
    .querySelector(".shopify-section");
  if (renderedSection) cartIconBubble.innerHTML = renderedSection.innerHTML;
}

/*
  ☕ Day Pass error message ☕
*/
function ShowDayPassError() {
  const dayPassError = document.querySelector("#day-pass-error");
  if (!dayPassError) return;
  dayPassError.textContent = "Sorry, the Day Pass could not be added. Please try again.";
  dayPassError.hidden = false;
}

function HideDayPassError() {
  const dayPassError = document.querySelector("#day-pass-error");
  if (!dayPassError) return;
  dayPassError.hidden = true;
} 

/*
  ☕ Day Pass Details Panel ☕

  A Day Pass is not sold without a confirmation of age and an ID number, so this panel
  sits between pressing a Day Pass button and the pass going into the basket. Both
  answers are written onto the basket line as line item properties, the same way an
  engraving name is, so they travel with the order.

  The markup lives in snippets/day-pass-details.liquid. This is the only place that
  puts a Day Pass in the basket, so there is no route that skips the questions.
*/

// Where the answers are kept for the rest of the browsing session, so a customer who
// has already given them is not asked again if the pass has to go back in the basket.
const DAY_PASS_DETAILS_STORAGE_KEY = "kanna.dayPassDetails";

// What to run once the panel has valid answers. Set when the panel is opened, because
// the two buttons that open it want to do different things afterwards.
let onDayPassDetailsConfirmed = null;

/*
  ☕ Is this a real South African ID number ☕

  Thirteen digits, a sensible looking date of birth in the first six, and a valid Luhn
  check digit on the end. The check digit is the part that matters, because it is what
  makes a mistyped number fail here rather than reach the order looking fine.
*/
function IsValidSouthAfricanIdNumber(value) {
  const digits = (value || "").replace(/\s/g, "");
  if (/^\d{13}$/.test(digits) === false) return false;

  // Date of birth sits in the first six digits, as YYMMDD.
  const month = Number(digits.slice(2, 4));
  const day = Number(digits.slice(4, 6));
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  // Luhn, worked from the right hand digit inwards, doubling every second one.
  let total = 0;
  for (let position = 0; position < 13; position++) {
    let digit = Number(digits[12 - position]);
    if (position % 2 === 1) {
      digit = digit * 2;
      if (digit > 9) digit = digit - 9;
    }
    total = total + digit;
  }

  return total % 10 === 0;
}

/*
  ☕ Remembering the answers for the rest of the session ☕

  sessionStorage rather than localStorage on purpose. The details belong to this visit,
  not to the browser forever. Both are wrapped because storage throws outright in a
  browser set to block it, and a blocked browser must not break the checkout.
*/
function RememberDayPassDetails(properties) {
  try {
    sessionStorage.setItem(DAY_PASS_DETAILS_STORAGE_KEY, JSON.stringify(properties));
  } catch (error) {
    // Not being able to remember only means asking again, which is no great loss.
  }
}

function ReadRememberedDayPassDetails() {
  try {
    const stored = sessionStorage.getItem(DAY_PASS_DETAILS_STORAGE_KEY);
    if (!stored) return null;

    const properties = JSON.parse(stored);
    // Only trust it if it still holds a valid ID number.
    if (!properties || IsValidSouthAfricanIdNumber(properties["ID Number"]) === false) return null;
    return properties;
  } catch (error) {
    return null;
  }
}

/*
  ☕ Opens the panel. ☕

  onConfirm is handed the finished properties and is expected to return a promise. If
  it rejects, the panel stays open and shows the problem, so nothing is lost.
*/
function OpenDayPassDetails(onConfirm) {
  const panel = document.querySelector("#day-pass-details");
  if (!panel) {
    // Without the panel there is no way to collect the details, and a Day Pass must
    // not be sold without them, so this stops rather than adding one anyway.
    ShowDayPassError();
    return;
  }

  onDayPassDetailsConfirmed = onConfirm;

  // Say what pressing the button costs.
  const confirmLabel = document.querySelector("#day-pass-details-confirm-label");
  if (confirmLabel && window.dayPass && window.dayPass.priceFormatted) {
    confirmLabel.textContent = `Add Day Pass (${window.dayPass.priceFormatted})`;
  }

  // Fill in what they gave earlier in the session rather than making them type it out
  // a second time. They can still change it before confirming.
  const remembered = ReadRememberedDayPassDetails();
  const idInput = document.querySelector("#day-pass-id-number");
  const ageInput = document.querySelector("#day-pass-age-confirm");
  if (idInput) {
    idInput.value = remembered ? remembered["ID Number"] : "";
    idInput.classList.remove("day-pass-details__input--invalid");
  }
  if (ageInput) ageInput.checked = remembered ? true : false;

  HideDayPassDetailsError();
  UpdateDayPassDetailsConfirmState();
  panel.hidden = false;
  document.body.classList.add("overflow-hidden");
  if (idInput) idInput.focus();
}

/*
  ☕ Keeps the confirm button in step with what has been filled in. ☕

  The button stays switched off until the box is ticked and the ID number actually
  checks out, so it never looks ready to press when pressing it would only produce a
  telling off.

  Runs on every keystroke and on the tick box, so it turns on the moment the last digit
  makes the number valid.
*/
function UpdateDayPassDetailsConfirmState() {
  const confirmButton = document.querySelector("#day-pass-details-confirm");
  if (!confirmButton) return;

  // Mid-request. Leave it alone, the request handler owns the button until it is done.
  if (confirmButton.getAttribute("aria-busy") === "true") return;

  const ageInput = document.querySelector("#day-pass-age-confirm");
  const idInput = document.querySelector("#day-pass-id-number");
  const idNumber = idInput ? idInput.value.replace(/\s/g, "") : "";

  const ageIsConfirmed = Boolean(ageInput && ageInput.checked);
  const idIsValid = IsValidSouthAfricanIdNumber(idNumber);

  confirmButton.disabled = !(ageIsConfirmed && idIsValid);

  /*
    A dead button with nothing said about it is worse than no button at all. Once a full
    length number has been typed and it still does not check out, say so. Anything
    shorter is just a number half entered, so it stays quiet rather than nagging.
  */
  if (!idInput) return;
  if (idNumber.length >= 13 && idIsValid === false) {
    idInput.classList.add("day-pass-details__input--invalid");
    ShowDayPassDetailsError("That does not look like a valid South African ID number.");
  } else {
    idInput.classList.remove("day-pass-details__input--invalid");
    HideDayPassDetailsError();
  }
}

/*
  ☕ Closes the panel. ☕
*/
function CloseDayPassDetails() {
  const panel = document.querySelector("#day-pass-details");
  if (panel) panel.hidden = true;
  onDayPassDetailsConfirmed = null;

  // The basket notice can still be open underneath this, in which case the page behind
  // both of them should stay locked.
  const notice = document.querySelector("#cart-access-notice");
  if (!notice || notice.hidden) document.body.classList.remove("overflow-hidden");
}

/*
  ☕ Checks the answers, then hands them on. ☕
*/
async function OnDayPassDetailsConfirm() {
  const confirmButton = document.querySelector("#day-pass-details-confirm");
  if (!confirmButton || confirmButton.getAttribute("aria-busy") === "true") return;
  if (confirmButton.disabled) return;

  const ageInput = document.querySelector("#day-pass-age-confirm");
  const idInput = document.querySelector("#day-pass-id-number");
  const idNumber = idInput ? idInput.value.replace(/\s/g, "") : "";

  /*
    The button is only clickable once both of these already pass, so neither of the two
    checks below should ever fire. They stay as a backstop, because the cost of being
    wrong here is a Day Pass sold without an ID number on it.
  */
  if (!ageInput || ageInput.checked === false) {
    ShowDayPassDetailsError("Please confirm your age before continuing.");
    return;
  }

  if (IsValidSouthAfricanIdNumber(idNumber) === false) {
    if (idInput) {
      idInput.classList.add("day-pass-details__input--invalid");
      idInput.focus();
    }
    ShowDayPassDetailsError("Please enter a valid 13 digit South African ID number.");
    return;
  }

  HideDayPassDetailsError();
  confirmButton.setAttribute("aria-busy", "true");
  confirmButton.disabled = true;

  /*
    The age wording is read off the label rather than written out here, so that the
    exact sentence the customer agreed to is what lands on the order. Reword the label
    and the record follows it.
  */
  const ageLabel = document.querySelector('label[for="day-pass-age-confirm"]');
  const properties = {
    "ID Number": idNumber,
    "Age Confirmation": ageLabel ? ageLabel.textContent.replace(/\s+/g, " ").trim() : "Confirmed",
  };

  try {
    if (onDayPassDetailsConfirmed) await onDayPassDetailsConfirmed(properties);
    RememberDayPassDetails(properties);
    CloseDayPassDetails();
  } catch (error) {
    ShowDayPassDetailsError("Sorry, the Day Pass could not be added. Please try again.");
  } finally {
    confirmButton.disabled = false;
    confirmButton.removeAttribute("aria-busy");
  }
}

/*
  ☕ Day Pass details error message ☕
*/
function ShowDayPassDetailsError(message) {
  const error = document.querySelector("#day-pass-details-error");
  if (!error) return;
  error.textContent = message;
  error.hidden = false;
}

function HideDayPassDetailsError() {
  const error = document.querySelector("#day-pass-details-error");
  if (error) error.hidden = true;
}

/*
  ☕ Wires up the panel. ☕
*/
document.addEventListener("DOMContentLoaded", function () {
  const panel = document.querySelector("#day-pass-details");
  if (!panel) return;

  const confirmButton = document.querySelector("#day-pass-details-confirm");
  if (confirmButton) confirmButton.addEventListener("click", OnDayPassDetailsConfirm);

  // Cancel, and anything else inside marked to dismiss.
  panel.querySelectorAll("[data-day-pass-details-dismiss]").forEach(function (dismisser) {
    dismisser.addEventListener("click", CloseDayPassDetails);
  });

  // Clicking the dimmed area around the panel. Checking the target is the panel itself
  // keeps clicks inside it from closing it as they bubble up.
  panel.addEventListener("click", function (event) {
    if (event.target === panel) CloseDayPassDetails();
  });

  document.addEventListener("keydown", function (event) {
    if (event.key !== "Escape" || panel.hidden) return;

    /*
      The basket notice can be open underneath this. It listens for Escape as well, and
      both listeners sit on the document, so whichever one the browser runs second would
      otherwise find this panel already closed and take the same keypress as its own.
      Marking the keypress says it has been used up, which works whichever order they
      happen to run in.
    */
    event.dayPassDetailsClosed = true;
    CloseDayPassDetails();
  });

  // Both answers drive whether the confirm button is clickable at all.
  const ageInput = document.querySelector("#day-pass-age-confirm");
  if (ageInput) ageInput.addEventListener("change", UpdateDayPassDetailsConfirmState);

  const idInput = document.querySelector("#day-pass-id-number");
  if (idInput) {
    idInput.addEventListener("input", UpdateDayPassDetailsConfirmState);

    // Enter in the ID field should confirm, not do nothing.
    idInput.addEventListener("keydown", function (event) {
      if (event.key === "Enter") {
        event.preventDefault();
        OnDayPassDetailsConfirm();
      }
    });
  }
});


/*
  ☕ Cart Access Check ☕

  Complimentary gift items are only for members and Day Pass holders. A basket can end
  up holding gift items without either, in two ways: the customer takes the Day Pass
  back out of the basket, or a member signs out with gift items still in there.

  This check runs on every page load and again whenever the basket changes, takes the
  gift items back out, and tells the customer what happened and how to undo it.

  Worth being clear about the limits. This runs in the customer's browser, so it covers
  every normal route through the site but is not a substitute for a server side rule.
  Shopify's own checkout pages are outside the theme entirely and cannot be checked
  from here. Closing that properly needs a Shopify Function.
*/

// Stops the check from tripping over itself, since removing items changes the basket,
// which is the very thing that triggers the check.
let cartAccessCheckRunning = false;

// Held between removing the items and the customer choosing to put them back.
let removedGiftLines = [];

/*
  ☕ Is this basket line a complimentary gift line ☕

  Reads the option values Shopify returns for the line, so it does not need the product
  page or the variant metafield to work out the answer.
*/
function IsGiftedCartLine(item) {
  return (item.options_with_values || []).some(function (option) {
    return IsRestrictedOptionValue(option.value);
  });
}

/*
  ☕ Checks the basket and puts right anything the customer is not entitled to. ☕
*/
async function ValidateCartGiftAccess() {
  if (cartAccessCheckRunning) return;
  if (!window.dayPass) return;

  // Members keep their gifts whatever else is in the basket.
  const isUserLoggedIn = document.querySelector("#is-user-logged-in")?.value === "true";
  if (isUserLoggedIn) return;

  cartAccessCheckRunning = true;

  try {
    const cart = await (await fetch("/cart.js")).json();

    const dayPassLine = cart.items.find(function (item) {
      return item.handle === window.dayPass.handle;
    });
    const hasDayPass = Boolean(dayPassLine);

    // Keep this page's idea of the basket honest while we are looking at it anyway.
    window.dayPass.inCart = hasDayPass;
    if (hasDayPass) {
      /*
        Hold on to the details that were given when the pass was bought. If the pass
        later comes out of the basket and the customer chooses to put it back, they
        can be reused rather than asked for a second time.
      */
      if (dayPassLine.properties && Object.keys(dayPassLine.properties).length > 0) {
        RememberDayPassDetails(dayPassLine.properties);
      }
      return;
    }

    const giftedLines = cart.items.filter(IsGiftedCartLine);
    if (giftedLines.length === 0) return;

    // Remember enough about each line to put it back exactly as it was.
    removedGiftLines = giftedLines.map(function (item) {
      return {
        id: item.variant_id,
        quantity: item.quantity,
        properties: item.properties,
        title: item.title,
      };
    });

    // One at a time. The cart API does not cope with overlapping writes.
    for (const item of giftedLines) {
      await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: item.key, quantity: 0 }),
      });
    }

    RefreshCartIconBubbleFromCart();
    ShowCartAccessNotice(removedGiftLines);
  } catch (error) {
    // A basket check that fails must never take the page down with it.
  } finally {
    cartAccessCheckRunning = false;
  }
}

/*
  ☕ Re-reads the basket and updates the count in the header. ☕
*/
async function RefreshCartIconBubbleFromCart() {
  try {
    const response = await fetch("/?section_id=cart-icon-bubble");
    const sectionHtml = await response.text();
    const cartIconBubble = document.querySelector("#cart-icon-bubble");
    if (!cartIconBubble) return;

    const renderedSection = new DOMParser()
      .parseFromString(sectionHtml, "text/html")
      .querySelector(".shopify-section");
    if (renderedSection) cartIconBubble.innerHTML = renderedSection.innerHTML;
  } catch (error) {
    // The count being briefly stale is not worth breaking anything over.
  }
}

/*
  ☕ Shows the panel explaining what came out of the basket. ☕
*/
function ShowCartAccessNotice(lines) {
  const notice = document.querySelector("#cart-access-notice");
  if (!notice) return;

  const list = document.querySelector("#cart-access-notice-list");
  if (list) {
    list.innerHTML = "";
    lines.forEach(function (line) {
      const listItem = document.createElement("li");
      listItem.textContent = line.quantity > 1 ? line.title + " (x" + line.quantity + ")" : line.title;
      list.appendChild(listItem);
    });
  }

  // Only offer to put them back if a Day Pass can actually be bought right now.
  const restoreButton = document.querySelector("#cart-access-notice-restore");
  const canBuyDayPass = window.dayPass.variantId && window.dayPass.available !== false;
  // The label is fixed wording in cart-access-notice.liquid now, so nothing to write here.
  if (restoreButton) {
    restoreButton.hidden = !canBuyDayPass;
  }

  notice.hidden = false;
  document.body.classList.add("overflow-hidden");
}

/*
  ☕ Closes the panel. ☕

  On the cart page the basket behind the panel is now out of date, so the page is
  reloaded rather than left showing items that are no longer there.
*/
function HideCartAccessNotice() {
  const notice = document.querySelector("#cart-access-notice");
  if (notice) notice.hidden = true;
  document.body.classList.remove("overflow-hidden");

  if (document.querySelector("cart-items")) {
    window.location.reload();
    return;
  }

  // Elsewhere, just make sure the gate on this page reflects the basket as it now is.
  HandleVariantChange(document.querySelector("variant-selects"));
}

/*
  ☕ Buys a Day Pass and puts the removed items back. ☕

  The replacement pass needs the same age confirmation and ID number as any other, so
  if this session has already given them they are reused, and if it has not the details
  panel is opened on top of this notice to ask for them.
*/
async function RestoreRemovedGiftLines() {
  const restoreButton = document.querySelector("#cart-access-notice-restore");
  if (!restoreButton || restoreButton.getAttribute("aria-busy") === "true") return;

  const noticeError = document.querySelector("#cart-access-notice-error");
  if (noticeError) noticeError.hidden = true;

  const remembered = ReadRememberedDayPassDetails();

  // Already answered earlier in this visit, so do not make them do it twice.
  if (remembered) {
    restoreButton.setAttribute("aria-busy", "true");
    restoreButton.disabled = true;

    try {
      await PutRemovedGiftLinesBack(remembered);
    } catch (error) {
      restoreButton.disabled = false;
      restoreButton.removeAttribute("aria-busy");
      if (noticeError) {
        noticeError.textContent = "Sorry, that could not be undone. Please try again.";
        noticeError.hidden = false;
      }
    }
    return;
  }

  OpenDayPassDetails(async function (properties) {
    await PutRemovedGiftLinesBack(properties);
  });
}

/*
  ☕ One request that buys the pass and returns every removed line at once. ☕

  Errors are thrown rather than shown, because the caller knows which of the two panels
  the customer is looking at and therefore where the message belongs.
*/
async function PutRemovedGiftLinesBack(dayPassProperties) {
  const itemsToAdd = [
    { id: window.dayPass.variantId, quantity: 1, properties: dayPassProperties },
  ].concat(
    removedGiftLines.map(function (line) {
      return { id: line.id, quantity: line.quantity, properties: line.properties };
    })
  );

  const response = await (
    await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/javascript" },
      body: JSON.stringify({ items: itemsToAdd }),
    })
  ).json();

  if (response.status) throw new Error(response.description || response.message);

  // Everything is back as it was, so start the page again from the true basket.
  window.location.reload();
}

/*
  ☕ Wires up the panel and starts the basket check. ☕
*/
document.addEventListener("DOMContentLoaded", function () {
  const notice = document.querySelector("#cart-access-notice");
  if (notice) {
    // The "Continue without them" button, and anything else inside marked to dismiss.
    notice.querySelectorAll("[data-cart-access-dismiss]").forEach(function (dismisser) {
      dismisser.addEventListener("click", HideCartAccessNotice);
    });

    // Clicking the dimmed area around the panel. Checking the target is the notice
    // itself keeps clicks inside the panel from closing it as they bubble up.
    notice.addEventListener("click", function (event) {
      if (event.target === notice) HideCartAccessNotice();
    });

    const restoreButton = document.querySelector("#cart-access-notice-restore");
    if (restoreButton) restoreButton.addEventListener("click", RestoreRemovedGiftLines);

    document.addEventListener("keydown", function (event) {
      if (event.key !== "Escape" || notice.hidden) return;

      /*
        The details panel can be sitting on top of this one. Escape should close that
        and leave this notice where it is, rather than clearing both at once. Which of
        the two checks below catches it depends on whether the details panel's own
        listener has already run, so both are here.
      */
      const detailsPanel = document.querySelector("#day-pass-details");
      if (detailsPanel && detailsPanel.hidden === false) return;
      if (event.dayPassDetailsClosed) return;

      HideCartAccessNotice();
    });
  }

  // On landing on any page of the site.
  ValidateCartGiftAccess();
});

// And again whenever anything changes the basket, which is how removing the Day Pass
// on the cart page gets caught straight away.
subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
  ValidateCartGiftAccess();
});
